import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Compass,
  Flame,
  Wind,
  Repeat,
  Trophy,
  Shield,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import SubHero from "@/components/layout/SubHero";
import NextStep from "@/components/layout/NextStep";
import { Reveal } from "@/components/landing/Shared";

const APPLICATIONS = [
  {
    key: "recovery",
    icon: Compass,
    title: "Recovery",
    lead:
      "For a nervous system that has adapted to substance-mediated regulation. Retraining the state, not white-knuckling the behavior.",
    to: "/applications/recovery",
  },
  {
    key: "stress-burnout",
    icon: Flame,
    title: "Stress & Burnout",
    lead:
      "For a physiology stuck in sustained activation, where rest no longer produces recovery.",
    to: "/applications/stress-burnout",
  },
  {
    key: "anxiety-regulation",
    icon: Wind,
    title: "Anxiety & Regulation",
    lead:
      "For patterns of autonomic hyperarousal that fire before conscious thought.",
    to: "/applications/anxiety-regulation",
  },
  {
    key: "habit-change",
    icon: Repeat,
    title: "Habit & Behavior Change",
    lead:
      "For behaviors that resist willpower — because the state driving them hasn’t changed.",
    to: "/applications/habit-change",
  },
  {
    key: "performance",
    icon: Trophy,
    title: "Performance",
    lead:
      "For high performers whose ceiling is set by autonomic capacity, not physical load.",
    to: "/applications/performance",
  },
  {
    key: "trauma-adaptation",
    icon: Shield,
    title: "Trauma Adaptation",
    lead:
      "For nervous systems carrying protective adaptations past their original context.",
    to: "/applications/trauma-adaptation",
  },
];

const ApplicationsHub = () => {
  return (
    <PageShell>
      <SubHero
        testId="applications-hero"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Applications" }]}
        overline="Applications"
        headline="Six contexts."
        headlineAccent="One methodology."
        lead="Applications are not separate treatments or programs. They are contexts in which the Adaptive Physiology framework is applied. In each, the same principle holds: change the physiological state, and the behavior follows."
        showAdaptiveLoop
        loopTestId="applications-hub-loop"
      />

      <section
        data-testid="applications-principle-section"
        className="relative py-16 sm:py-20 forr-canvas-alt border-b border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl">
              {[
                "The nervous system adapts.",
                "State influences behavior.",
                "Repeated inputs create adaptations.",
                "Intentional inputs create new adaptations.",
              ].map((p, i) => (
                <div key={p} className="flex items-start gap-3">
                  <span className="font-display forr-text-teal-deep text-sm font-medium mt-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="forr-text-ink font-display text-[15px] leading-snug tracking-tight">
                    {p}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section
        data-testid="applications-grid-section"
        className="relative py-24 sm:py-32 forr-canvas"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {APPLICATIONS.map((a, i) => (
              <Reveal delay={i * 0.05} key={a.key}>
                <Link
                  to={a.to}
                  data-testid={`applications-card-${a.key}`}
                  className="forr-card rounded-[24px] p-8 h-full block group"
                >
                  <div className="flex items-center justify-between mb-8">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        color: "var(--forr-teal-deep)",
                      }}
                    >
                      <a.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[11px] uppercase tracking-[0.24em] forr-text-faint">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display forr-text-ink text-2xl font-medium tracking-tight mb-3">
                    {a.title}
                  </h3>
                  <p className="text-sm font-light forr-text-soft leading-relaxed">
                    {a.lead}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 forr-text-teal-deep text-sm font-medium">
                    Explore
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline="Return"
        title="The Methodology"
        description="Understand the framework these six applications share."
        to="/methodology"
        cta="Open the methodology"
        testId="applications-hub-next"
      />
    </PageShell>
  );
};

export default ApplicationsHub;
